//Built-in libraries

//Third party libraries
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
// import { compareTwoArrays, isObjectEmpty } from '@/utils';
import { signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
//Custom components
import Button from '@/components/UI/Button';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { type UploadFnReturn, useFileUpload } from '@/hooks/useFileUpload';

//STP Libraries

import type { ISignApplicationPublicAssetPayload } from '@/api/Rover/ApplicationForm/types';
import { HookRadioGroup } from '@/components/UI/HookForm/Form/RadioGroup';
//Assets and Miscellaneous
import type { SignedApplicationAssetResponse } from '@/types/Shared';
import EstimatedProjectBudget from './EstimatedProjectBudget';

//Component Begin

export default function ProjectBudget() {
  const _params = useParams<string>();
  const [uploadError, setUploadError] = useState<string>('');
  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const { setCurrentStep, updateData, data: formContextData } = useApplicationFormContext();

  const {
    trigger,
    getValues,
    setValue,
    formState: { errors, isValid },
    setFocus,
  } = useFormContext();
  const { step2 } = getValues();
  const uploadFile = useMutation((payload: { signAssetPayload: ISignApplicationPublicAssetPayload[]; files: File[] }) => upload(payload), {
    onSuccess: ({ sign, files }: UploadFnReturn) => {
      let signRes: SignedApplicationAssetResponse[] = [];
      if (Array.isArray(sign)) {
        signRes = [...sign];
      } else {
        signRes = [{ ...sign }];
      }

      updateData({
        step2: {
          ...getValues('step2'),
          additionalDocs: signRes.map((sig, index) => ({
            name: files[index].name,
            path: sig.path,
            key: sig.key,
            type: files[index].type,
            field: 'additionalDocs',
            description: 'Additional Docs',
          })),
        },
      });
      setCurrentStep((prev) => prev + 1);
    },
    onError: (_error: any) => {
      setUploadError('Failed to upload document');
    },
  });

  // useEffect(() => {
  //   if (!isObjectEmpty(errors)) {
  //     const firstErrorKey = Object.keys(errors?.step2)[0];
  //     setFocus(`step2.${firstErrorKey}`);
  //   }
  // }, [errors, setFocus]);
  const [formError, setFormError] = useState({ estimatedProjectBudget: '' });
  useEffect(() => {
    if (formContextData.step2.estimatedProjectBudget.length > 0) {
      setFormError((prev) => {
        return { ...prev, estimatedProjectBudget: '' };
      });
    }
  }, [formContextData]);

  const onSubmit = () => {
    trigger();
    if (!isValid) {
      return;
    }

    if (formContextData.step2.estimatedProjectBudget.length === 0) {
      setFormError({
        estimatedProjectBudget: 'At least one is required',
      });
    } else {
      updateData({
        step2: {
          ...step2,
          estimatedProjectBudget: formContextData?.step2?.estimatedProjectBudget,
        },
      });
      setCurrentStep((prev) => prev + 1);
    }

    // const formData = getValues('step2');

    // if (formData.selectedFiles?.length) {
    //   const formDataSelectedFiles = formData?.selectedFiles.map(
    //     (f: File) => f.name
    //   );
    //   const formContextDataUploadedFiles =
    //     formContextData?.step2?.additionalDocs.map((f: File) => f.name);

    //   const fileUploadCompare = compareTwoArrays(
    //     formDataSelectedFiles,
    //     formContextDataUploadedFiles
    //   );

    //   if (fileUploadCompare.array1Diff.length > 0) {
    //     const signAssetPayload = formData.selectedFiles
    //       .filter((f: File) => fileUploadCompare.array1Diff.includes(f.name))
    //       .map((file: File) => ({
    //         fileName: file.name,
    //         fileType: file.type,
    //         fileSize: file.size,
    //         formId: String(params.applicationFormId),
    //       }));
    //     uploadFile.mutate({
    //       signAssetPayload,
    //       files: formData.selectedFiles,
    //     });
    //   } else {
    //     setCurrentStep((prev) => prev + 1);
    //   }
    // } else {
    //   updateData({
    //     step2: { ...formData },
    //   });
    //   setCurrentStep((prev) => prev + 1);
    // }
  };

  return (
    <div className='p-6 space-y-6 sm:rounded-r-md sm:overflow-hidden'>
      <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>Project Budget</h3>
      <div className='grid grid-cols-6 gap-6'>
        <div className='col-span-6'>
          <EstimatedProjectBudget formValues={step2} />
          {formError.estimatedProjectBudget.length > 0 && (
            <span className='text-tertiaryColor-normal'>{formError?.estimatedProjectBudget}</span>
          )}
        </div>
        <div className='col-span-6'>
          <HookFileInput
            name='step2.selectedFiles'
            label='Attach a listing of the equipment and furnishing not fixed which includes the cost of each item'
            id='selectedFiles'
            placeholder='Upload Documents'
            multiple
          />
        </div>
        {uploadError && <p className='text-tertiaryColor-normal text-md col-span-6'>{uploadError}</p>}
        <HookRadioGroup
          className='col-span-6 md:col-span-6'
          label='Is the construction contract at least $250,000 and will State funds be used to pay for 25% or more of the contract ?'
          name='step2.q1'
          id='step2-q1'
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
          ]}
        />
        <HookRadioGroup
          className='col-span-6 md:col-span-6'
          label='Have you received $1,000,000 or more and is the construction contract $500,000 or more ?'
          name='step2.q2'
          id='step2-q2'
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
          ]}
        />
      </div>
      <div className='flex gap-4 justify-end mt-5'>
        <Button label='Previous' btnStyle='neutral' type='button' onClick={() => setCurrentStep((prev) => prev - 1)} />
        <Button
          label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
          btnStyle={uploadFile?.isLoading ? 'neutral' : 'primary'}
          disabled={uploadFile.isLoading}
          type='button'
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
